import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, Car, ShieldCheck } from "lucide-react";
import { AvianLogo } from "./AvianLogo";
import { useAuth } from "../lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navLink = ({ isActive }) =>
    `relative text-[15px] font-semibold transition-colors ${
      isActive ? "text-[#1d7ffd]" : "text-[#0a1f4d] hover:text-[#1d7ffd]"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1fr_auto] lg:grid-cols-3 items-center gap-4">
        <AvianLogo size={34} />
        <nav className="hidden md:flex items-center justify-center gap-6 lg:gap-10 whitespace-nowrap">
          <NavLink to="/vehicles" className={navLink} data-testid="nav-vehicles">
            Cars
          </NavLink>
          <NavLink to="/how-it-works" className={navLink} data-testid="nav-how">
            How it works
          </NavLink>
          <NavLink to="/host" className={navLink} data-testid="nav-host">
            List your car
          </NavLink>
          <NavLink to="/trust" className={navLink} data-testid="nav-trust">
            Help
          </NavLink>
        </nav>

        <div className="hidden md:flex items-center justify-end gap-3 lg:gap-4 whitespace-nowrap">

          {user && user.id ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full border border-[#E2E8F0] pl-3 pr-3.5 py-1.5 hover:shadow-md transition-shadow"
                  data-testid="user-menu-btn"
                >
                  <span className="w-7 h-7 rounded-full bg-[#08153a] text-white text-xs font-bold flex items-center justify-center">
                    {(user.name || "U").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold text-[#08153a]">{(user.name || "Account").split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-[#64748B]">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard size={16} className="mr-2" /> My trips
                </DropdownMenuItem>
                {(user.role === "owner" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/owner")} data-testid="menu-owner">
                    <Car size={16} className="mr-2" /> Host dashboard
                  </DropdownMenuItem>
                )}
                {(user.role === "driver" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/driver")} data-testid="menu-driver">
                    <Car size={16} className="mr-2" /> Chauffeur dashboard
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck size={16} className="mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="menu-profile">
                  <User size={16} className="mr-2" /> Profile & verification
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="text-[15px] font-semibold text-[#0a1f4d] hover:text-[#1d7ffd] px-3 py-2" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/register" className="btn-navy text-[15px]" data-testid="nav-signup">
                Sign up
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 text-[#08153a] justify-self-end"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E2E8F0] bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            <Link to="/vehicles" onClick={() => setOpen(false)} className="text-[#08153a] font-semibold">Cars</Link>
            <Link to="/how-it-works" onClick={() => setOpen(false)} className="text-[#08153a] font-semibold">How it works</Link>
            <Link to="/trust" onClick={() => setOpen(false)} className="text-[#08153a] font-semibold">Help</Link>
            <Link to="/host" onClick={() => setOpen(false)} className="text-[#08153a] font-semibold">List your car</Link>
            {user && user.id ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-[#08153a] font-semibold">Dashboard</Link>
                <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-left text-[#08153a] font-semibold">Log out</button>
              </>
            ) : (
              <div className="flex gap-3 pt-2">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline text-sm flex-1 text-center">Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-navy text-sm flex-1 text-center">Sign up</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
